import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import {
  AiIcon,
  AllDocsIcon,
  CloseIcon,
  DeleteIcon,
  EdgelessIcon,
  EmailIcon,
  GridIcon,
  JournalIcon,
  PublishIcon,
  SettingsIcon,
  TagIcon,
  TodayIcon,
  ViewLayersIcon,
} from '@blocksuite/icons/rc';
import * as Dialog from '@radix-ui/react-dialog';
import { useLiveData, useService } from '@toeverything/infra';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTransition } from 'react-transition-state';

import {
  DocSwitcherService,
  type WorkspaceSwitcherDestination,
  type WorkspaceSwitcherKind,
} from '../services/doc-switcher';
import * as styles from './doc-switcher.css';

const ANIMATION_TIMEOUT = 100;

// --- Card Component ---

interface DocSwitcherCardProps {
  destination: WorkspaceSwitcherDestination & { docId: string };
  selected: boolean;
  onSelect: (destination: WorkspaceSwitcherDestination) => void;
  onRemove: (key: string, e: React.MouseEvent) => void;
}

const DocSwitcherCard = ({
  destination,
  selected,
  onSelect,
  onRemove,
}: DocSwitcherCardProps) => {
  const docDisplayMeta = useService(DocDisplayMetaService);
  const t = useI18n();
  const { docId } = destination;

  const title = useLiveData(docDisplayMeta.title$(docId));
  const Icon = useLiveData(docDisplayMeta.icon$(docId));

  return (
    <div
      className={styles.card}
      data-selected={selected}
      onClick={() => onSelect(destination)}
      role="option"
      aria-selected={selected}
    >
      <div className={styles.cardPreview}>
        <div className={styles.previewIcon}>{Icon && <Icon />}</div>
        <button
          className={styles.removeButton}
          onClick={e => onRemove(destination.key, e)}
          aria-label={t['Delete']()}
        >
          <CloseIcon className={styles.removeIcon} />
        </button>
      </div>

      {/* Label: icon + title */}
      <div className={styles.cardLabel}>
        <span className={styles.cardIcon}>{Icon && <Icon />}</span>
        <span className={styles.cardTitle}>{title || t['Untitled']()}</span>
      </div>
    </div>
  );
};

interface DestinationCardProps {
  destination: WorkspaceSwitcherDestination;
  icon: ReactElement;
  label: string;
  selected: boolean;
  onSelect: (destination: WorkspaceSwitcherDestination) => void;
  onRemove: (key: string, e: React.MouseEvent) => void;
}

const DestinationCard = ({
  destination,
  icon,
  label,
  selected,
  onSelect,
  onRemove,
}: DestinationCardProps) => {
  const t = useI18n();
  return (
    <div
      className={styles.card}
      data-selected={selected}
      onClick={() => onSelect(destination)}
      role="option"
      aria-selected={selected}
    >
      <div className={styles.cardPreview}>
        <div className={styles.previewIcon}>{icon}</div>
        <button
          className={styles.removeButton}
          onClick={e => onRemove(destination.key, e)}
          aria-label={t['Delete']()}
        >
          <CloseIcon className={styles.removeIcon} />
        </button>
      </div>
      <div className={styles.cardLabel}>
        <span className={styles.cardIcon}>{icon}</span>
        <span className={styles.cardTitle}>{label}</span>
      </div>
    </div>
  );
};

const getDestinationMeta = (
  kind: WorkspaceSwitcherKind,
  t: ReturnType<typeof useI18n>
): { icon: ReactElement; label: string } => {
  switch (kind) {
    case 'mail':
      return {
        icon: <EmailIcon />,
        label: t['com.streamlain.rootAppSidebar.email'](),
      };
    case 'planner':
      return {
        icon: <TodayIcon />,
        label: t['com.streamlain.rootAppSidebar.planner'](),
      };
    case 'journal':
      return {
        icon: <JournalIcon />,
        label: t['com.streamlain.journal.app-sidebar-title'](),
      };
    case 'workflows':
      return {
        icon: <EdgelessIcon />,
        label: t['com.streamlain.streamlain.workflow.page.title'](),
      };
    case 'hub':
      return {
        icon: <PublishIcon />,
        label: t['com.streamlain.rootAppSidebar.hub'](),
      };
    case 'apps':
      return {
        icon: <GridIcon />,
        label: t['com.streamlain.rootAppSidebar.appsSection'](),
      };
    case 'graph':
      return {
        icon: <ViewLayersIcon />,
        label: t['com.streamlain.graph-explorer.title'](),
      };
    case 'chat':
      return {
        icon: <AiIcon />,
        label: t['com.streamlain.ai.chat.input.agent-mode.chat'](),
      };
    case 'settings':
      return {
        icon: <SettingsIcon />,
        label: t['com.streamlain.rootAppSidebar.settings'](),
      };
    case 'trash':
      return { icon: <DeleteIcon />, label: t['Trash']() };
    case 'collection':
      return { icon: <ViewLayersIcon />, label: t['Collections']() };
    case 'tag':
      return { icon: <TagIcon />, label: t['Tags']() };
    case 'documents':
      return { icon: <AllDocsIcon />, label: t['All pages']() };
    case 'doc':
      return { icon: <AllDocsIcon />, label: t['All pages']() };
  }
};

// --- Container Component ---

export const DocSwitcherContainer = () => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const t = useI18n();
  const docSwitcher = useService(DocSwitcherService);
  const workbench = useService(WorkbenchService).workbench;
  const recentDestinations = useLiveData(docSwitcher.availableDestinations$);
  const ctrlHeldRef = useRef(false);
  const openRef = useRef(false);

  const [{ status }, toggle] = useTransition({
    timeout: ANIMATION_TIMEOUT,
  });

  useEffect(() => {
    toggle(open);
  }, [open, toggle]);

  useEffect(() => {
    docSwitcher.pruneMissingDocs();
  }, [docSwitcher, recentDestinations]);

  useEffect(() => {
    setSelectedIndex(prev =>
      recentDestinations.length === 0
        ? 0
        : Math.min(prev, recentDestinations.length - 1)
    );
  }, [recentDestinations.length]);

  // Navigate to the selected workbench destination and close.
  const navigateAndClose = useCallback(
    (destination: WorkspaceSwitcherDestination) => {
      workbench.open(destination.to);
      setOpen(false);
      openRef.current = false;
      ctrlHeldRef.current = false;
    },
    [workbench]
  );

  // Handle Ctrl+^ keydown to open/cycle
  // German ISO keyboards: ^ is 'IntlBackslash' (key left of 1)
  // US/UK ANSI keyboards: ` is 'Backquote' (same physical position)
  useEffect(() => {
    const isSwitcherKey = (e: KeyboardEvent) =>
      e.code === 'IntlBackslash' || e.code === 'Backquote';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && isSwitcherKey(e)) {
        e.preventDefault();
        e.stopPropagation();

        if (!openRef.current) {
          // First press: open the switcher
          const destinations = docSwitcher.availableDestinations$.value;
          if (destinations.length < 2) return;
          openRef.current = true;
          ctrlHeldRef.current = true;
          setSelectedIndex(1); // Start at second (previous) doc
          setOpen(true);
        } else {
          // Subsequent presses: cycle forward
          setSelectedIndex(prev => {
            const destinations = docSwitcher.availableDestinations$.value;
            return (prev + 1) % destinations.length;
          });
        }
      }

      // Track Ctrl held state
      if (e.key === 'Control') {
        ctrlHeldRef.current = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // When Ctrl is released and switcher is open, navigate to selection
      if (e.key === 'Control' && openRef.current) {
        ctrlHeldRef.current = false;
        const destinations = docSwitcher.availableDestinations$.value;
        const destination = destinations[selectedIndex];
        if (destination) {
          navigateAndClose(destination);
        } else {
          setOpen(false);
          openRef.current = false;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [docSwitcher, selectedIndex, navigateAndClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        openRef.current = false;
        ctrlHeldRef.current = false;
      }
    };
    window.addEventListener('keydown', handleEsc, true);
    return () => window.removeEventListener('keydown', handleEsc, true);
  }, [open]);

  const handleSelect = useCallback(
    (destination: WorkspaceSwitcherDestination) => {
      navigateAndClose(destination);
    },
    [navigateAndClose]
  );

  const handleRemove = useCallback(
    (key: string, e: React.MouseEvent) => {
      e.stopPropagation();
      docSwitcher.removeDestination(key);
      const destinations = docSwitcher.availableDestinations$.value;
      if (destinations.length === 0) {
        setOpen(false);
        openRef.current = false;
        return;
      }
      setSelectedIndex(prev => Math.min(prev, destinations.length - 1));
    },
    [docSwitcher]
  );

  if (status === 'exited' && !open) return null;

  return (
    <Dialog.Root
      modal
      open={status !== 'exited'}
      onOpenChange={newOpen => {
        if (!newOpen) {
          setOpen(false);
          openRef.current = false;
          ctrlHeldRef.current = false;
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <div className={styles.contentWrapper}>
          <Dialog.Content
            style={assignInlineVars({
              [styles.animationTimeout]: `${ANIMATION_TIMEOUT}ms`,
            })}
            className={styles.content}
            data-state={status}
            aria-describedby={undefined}
            onPointerDownOutside={e => {
              e.preventDefault();
              setOpen(false);
              openRef.current = false;
              ctrlHeldRef.current = false;
            }}
          >
            <Dialog.Title
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {t['com.streamlain.keyboardShortcuts.switch']()}
            </Dialog.Title>
            {recentDestinations.length === 0 ? (
              <div className={styles.emptyState}>
                {t['com.streamlain.selectPage.empty']()}
              </div>
            ) : (
              recentDestinations.map((destination, index) => {
                if (destination.kind === 'doc' && destination.docId) {
                  return (
                    <DocSwitcherCard
                      key={destination.key}
                      destination={{ ...destination, docId: destination.docId }}
                      selected={index === selectedIndex}
                      onSelect={handleSelect}
                      onRemove={handleRemove}
                    />
                  );
                }

                const { icon, label } = getDestinationMeta(destination.kind, t);
                return (
                  <DestinationCard
                    key={destination.key}
                    destination={destination}
                    icon={icon}
                    label={destination.title || label}
                    selected={index === selectedIndex}
                    onSelect={handleSelect}
                    onRemove={handleRemove}
                  />
                );
              })
            )}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
